import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIsManagement } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  dashboardQuery,
  jobsQuery,
  projectsQuery,
  inventoryListQuery,
  warehouseQuery,
  calendarMonthQuery,
  calendarWeekQuery,
  startOfWeek,
} from '@/lib/pageQueries';

// The daily-loop route chunks. Warming these means the first click into a
// section doesn't wait on JavaScript either. Detail pages are included for
// their code only — their data is per-record and fetched on open.
const PAGE_CHUNKS: Array<() => Promise<unknown>> = [
  () => import('@/pages/Jobs/JobListPage'),
  () => import('@/pages/Jobs/JobDetailPage'),
  () => import('@/pages/Inventory/InventoryListPage'),
  () => import('@/pages/Inventory/InventoryDetailPage'),
  () => import('@/pages/Warehouse/WarehousePage'),
  () => import('@/pages/Warehouse/WarehouseJobPage'),
  () => import('@/pages/CalendarPage'),
  () => import('@/pages/Projects/ProjectListPage'),
  () => import('@/pages/Projects/ProjectDetailPage'),
];

/**
 * Warm the cache for the main sections shortly after sign-in, so the first
 * click into each one renders instantly instead of fetching on demand.
 *
 * Waits 2s after the shell mounts so whatever page the user actually opened
 * wins the bandwidth race. Role-aware: staff aren't prefetched management-only
 * data (the requests would just 403). Failures are silently ignored — this is
 * an optimisation, and the page will fetch normally on visit if it missed.
 */
export function usePrefetchAppData() {
  const queryClient = useQueryClient();
  const isManagement = useIsManagement();
  const isMobile = useIsMobile();

  useEffect(() => {
    const timer = setTimeout(() => {
      const now = new Date();
      const defs: Array<{ key: readonly unknown[]; fetch: () => Promise<unknown> }> = [
        dashboardQuery,
        jobsQuery,
        inventoryListQuery,
        warehouseQuery,
        isMobile
          ? calendarWeekQuery(startOfWeek(now))
          : calendarMonthQuery(now.getFullYear(), now.getMonth() + 1),
        ...(isManagement ? [projectsQuery] : []),
      ];
      for (const def of defs) {
        queryClient.prefetchQuery({ queryKey: def.key as unknown[], queryFn: def.fetch });
      }
      for (const load of PAGE_CHUNKS) {
        load().catch(() => { /* chunk warms on real navigation instead */ });
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [queryClient, isManagement, isMobile]);
}
